import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { opportunitiesApi, OpportunitiesResponse } from "../api/opportunities";
import { OpportunityRecord } from "../pages/tender/helper/opportunities/types";
import { REGIONS } from "../utils/constants";

/**
 * Query keys for opportunity-related queries
 * Centralized to ensure consistency across the app
 */
export const OPPORTUNITIES_QUERY_KEYS = {
  all: ["opportunities"] as const,
  list: () => ["opportunities", "list"] as const,
  byId: (id: string) => ["opportunities", "id", id] as const,
};

/**
 * Transform BC opportunity data to OpportunityRecord format
 */
const transformBCOpportunityToRecord = (bcOpp: any): OpportunityRecord => {
  const opportunityNo = bcOpp.no || "";
  const description = bcOpp.description || "Unnamed Project";
  const contactCompanyName = bcOpp.contactCompanyName || bcOpp.contactName || "-";
  const estimatedValue = bcOpp.estimatedValueLCY || 0;
  const creationDate = bcOpp.creationDate || new Date().toISOString().split("T")[0];
  const dateClosed = bcOpp.dateClosed || bcOpp.estimatedClosingDate || creationDate;
  const salespersonName = bcOpp.salespersonName || "Unknown";
  const siteAddress = bcOpp.siteAddress || "";

  // Extract region from BC opportunity
  // Format: "Project Region 40 - Yorkshire" -> Extract "Yorkshire"
  let region = "";
  if (bcOpp.region && typeof bcOpp.region === "string") {
    if (bcOpp.region.includes(" - ")) {
      // Extract the part after " - "
      region = bcOpp.region.split(" - ").pop()?.trim() || "";
    } else {
      region = bcOpp.region;
    }
  }

  // Validate region against REGIONS list
  if (region && !REGIONS.includes(region)) {
    // Try to find a matching region (case-insensitive)
    const matchingRegion = REGIONS.find(
      (r) => r.toLowerCase() === region.toLowerCase()
    );
    if (matchingRegion) {
      region = matchingRegion;
    } else {
      console.warn(`Region "${region}" from BC not found in REGIONS list, setting to empty`);
      region = "";
    }
  }

  // Map BC status to application status
  const status = bcOpp.status || "In-progress";

  return {
    key: `bc_opp_${opportunityNo}`,
    no: opportunityNo,
    tenderId: opportunityNo,
    tender: description,
    client: contactCompanyName,
    siteAddress: siteAddress,
    region: region,
    totalBudget: estimatedValue,
    rsBudget: 0, // BC doesn't split by RS/GW, set to 0
    gwBudget: 0, // BC doesn't split by RS/GW, set to 0
    creationDate: creationDate,
    lastUpdateDate: dateClosed,
    status: status,
    creator: salespersonName,
  };
};

/**
 * Hook to fetch all opportunities from BC
 * @param options - Query options (enabled, refetchInterval, etc.)
 * @returns Query result with list of opportunities
 */
export function useOpportunities(
  options?: Omit<
    UseQueryOptions<OpportunityRecord[], Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<OpportunityRecord[], Error>({
    queryKey: OPPORTUNITIES_QUERY_KEYS.list(),
    queryFn: async () => {
      const response = await opportunitiesApi.getAll();

      // BC API returns data in response.data.value array
      const opportunities = response.value || [];

      // Transform BC opportunities to OpportunityRecord format
      return opportunities.map(transformBCOpportunityToRecord);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    ...options,
  });
}

/**
 * Hook to fetch a specific opportunity by ID
 * @param opportunityId - The opportunity ID
 * @param options - Query options
 * @returns Query result with the opportunity
 */
export function useOpportunity(
  opportunityId: string,
  options?: Omit<
    UseQueryOptions<OpportunityRecord, Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<OpportunityRecord, Error>({
    queryKey: OPPORTUNITIES_QUERY_KEYS.byId(opportunityId),
    queryFn: async () => {
      const bcOpp = await opportunitiesApi.getById(opportunityId);
      return transformBCOpportunityToRecord(bcOpp);
    },
    enabled: !!opportunityId && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}
